import { Controller } from "@hotwired/stimulus";

// Share License's recipients box (TMS-1681). As the box changes, the list is checked on the
// server (the same reading as the admin bulk grant) and the answer drawn here: the licences that
// would be used, each flagged address and why it isn't counted, and an error when there are more
// recipients than licences left. Send License waits for at least one valid recipient and none
// over. Upload CSV reads a file the same way and adds its addresses to the box.
export default class extends Controller {
  static targets = ["form", "input", "file", "counter", "flags", "error", "submit"];
  static values = { checkUrl: String, total: Number, used: Number };

  static DEBOUNCE_MS = 250;

  connect() {
    this.requestId = 0;
    if (this.hasInputTarget && this.inputTarget.value.trim()) this.runCheck();
  }

  disconnect() {
    clearTimeout(this.timer);
  }

  check() {
    clearTimeout(this.timer);
    this.setSubmittable(false);
    this.timer = setTimeout(() => this.runCheck(), this.constructor.DEBOUNCE_MS);
  }

  pickFile() {
    this.fileTarget.click();
  }

  async upload() {
    const file = this.fileTarget.files[0];
    if (!file) return;

    const body = new FormData();
    body.append("file", file);
    const plan = await this.post(body);
    this.fileTarget.value = "";
    if (!plan || plan.error) return;

    const current = this.inputTarget.value.replace(/\s+$/, "");
    this.inputTarget.value = current ? `${current}\n${plan.text}` : plan.text;
    this.runCheck();
  }

  submit(event) {
    if (this.hasSubmitTarget && this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
  }

  async runCheck() {
    const body = new FormData();
    body.append("recipients", this.inputTarget.value);
    const plan = await this.post(body);
    if (plan && !plan.error) this.draw(plan);
  }

  // Answers to an older box are dropped: only the latest request draws.
  async post(body) {
    const requestId = ++this.requestId;
    let response;
    try {
      response = await fetch(this.checkUrlValue, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        credentials: "same-origin"
      });
    } catch {
      if (requestId === this.requestId) this.showError("We couldn’t check the list. Please try again.");
      return null;
    }
    if (requestId !== this.requestId) return null;

    const plan = await response.json().catch(() => ({ error: "We couldn’t check the list. Please try again." }));
    if (plan.error) {
      this.showError(plan.error);
      this.setSubmittable(false);
    }
    return plan;
  }

  draw(plan) {
    const valid = plan.valid_emails.length;
    const used = plan.used + valid;
    this.counterTarget.textContent = `${used} of ${plan.total} licenses used`;
    this.counterTarget.classList.toggle("is-over", plan.over_by > 0);

    const flagged = plan.rows.filter((row) => row.status !== "valid");
    this.flagsTarget.replaceChildren(...flagged.map((row) => this.flagItem(row)));
    this.flagsTarget.hidden = flagged.length === 0;

    if (plan.over_by > 0) {
      const left = plan.total - plan.used;
      this.showError(
        `You have ${left} ${left === 1 ? "license" : "licenses"} left. ` +
        `Remove ${plan.over_by} ${plan.over_by === 1 ? "recipient" : "recipients"} to continue.`
      );
    } else {
      this.hideError();
    }
    this.setSubmittable(valid > 0 && plan.over_by === 0);
  }

  flagItem(row) {
    const item = document.createElement("li");
    item.className = `vm-share-license-flag vm-share-license-flag--${row.status}`;
    const icon = document.createElement("i");
    icon.className = "ph ph-warning-circle";
    icon.setAttribute("aria-hidden", "true");
    const email = document.createElement("span");
    email.className = "vm-share-license-flag-email";
    email.textContent = row.email || "(no email)";
    const reason = document.createElement("span");
    reason.className = "vm-share-license-flag-reason";
    reason.textContent = row.message;
    item.append(icon, email, reason);
    return item;
  }

  setSubmittable(on) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !on;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  hideError() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }
}
